import { t } from '@lingui/macro'

import { mixpanel } from '@/mixpanel'
import { getMedicationDosageText } from '@/modules/app/utils/medication.utils'
import { useQuestionnaireTrackingProps } from '@/modules/questionnaire/hooks/useQuestionnaireTrackingProps'
import { StructuredObservation } from '../structured-observation/StructuredObservation'
import { type ObservationAction, useDiffTrackedObservations } from '../useDiffTrackedObservations'
import { type IdentifiedMedicationStatement, MedicationContext } from './MedicationQuestion.context'
import { resolveDosageMode } from './MedicationQuestion.helpers'
import { MedicationItemForm } from './medication-form/MedicationItemForm'

import type {
  MedicationScheme,
  MedicationSchemeStatus,
  MedicationStatement,
} from '@/modules/app/types/HealthParameter.types'

// ================================ Types ================================

type MedicationQuestionProps = {
  questionId: string
  value?: MedicationScheme
  schemeStatus: MedicationSchemeStatus
  onSubmitAndAdvance: (value: MedicationScheme) => void
}

// ================================ Helpers ================================

const getDosageSubtitle = (medication: IdentifiedMedicationStatement): string | undefined => {
  return getMedicationDosageText(medication) || undefined
}

/** Mixpanel `source` property for a medication entry. */
const getTrackingSource = (medication: MedicationStatement): 'zorgi' | 'synops' | 'free_text' => {
  if (medication.code?.system === 'zorgi-article') return 'zorgi'
  if (medication.synopsId) return 'synops'
  return 'free_text'
}

// ================================ Component ================================

export const MedicationQuestion = ({
  questionId,
  value,
  schemeStatus: initialSchemeStatus,
  onSubmitAndAdvance: submitAndAdvance,
}: MedicationQuestionProps) => {
  const trackingProps = useQuestionnaireTrackingProps()

  const trackEntrySaved = (action: ObservationAction, medication: MedicationStatement) => {
    mixpanel.track('medication_entry_saved', {
      action,
      source: getTrackingSource(medication),
      dosage_mode: resolveDosageMode(medication) === 'structured' ? 'structured' : 'free_text',
      ...trackingProps,
    })
  }

  const {
    identified: identifiedMedications,
    visible: visibleMedications,
    schemeStatus,
    view,
    addEntry: addMedication,
    editEntry: editMedication,
    removeEntry: removeMedication,
    confirmEmpty: confirmNoMedication,
    setView: handleSetView,
    onSubmitAndAdvance: handleSubmitAndAdvance,
  } = useDiffTrackedObservations<MedicationStatement, MedicationScheme>({
    questionId,
    listKey: 'medications',
    fallbackEntries: value?.medications ?? [],
    initialSchemeStatus,
    // Server-origin identity fields kept from the existing entry on edit.
    identityKeys: ['synopsId', 'source', 'status'],
    buildPrevious: (medication) => ({
      name: medication.name,
      dosage: medication.dosage,
    }),
    trackEntrySaved,
    submitAndAdvance,
  })

  // -- Render -----------------------------------------------------------------

  return (
    <MedicationContext.Provider
      value={{
        questionId,
        medications: identifiedMedications,
        schemeStatus,
        addMedication,
        editMedication,
        removeMedication,
        confirmNoMedication,
        setView: handleSetView,
        onSubmitAndAdvance: handleSubmitAndAdvance,
      }}
    >
      {view === 'choice' ? (
        <StructuredObservation.Choice
          questionId={questionId}
          emptyMessage={t({
            id: 'medication.emptyState',
            message: 'Nog geen medicatiegegevens gekend',
          })}
          addLabel={t({
            id: 'medication.choice.add',
            message: 'Ik wil medicatie toevoegen',
          })}
          noneLabel={t({
            id: 'medication.choice.none',
            message: 'Ik neem geen medicatie',
          })}
          onAdd={() => handleSetView('form')}
          onConfirmNone={confirmNoMedication}
        />
      ) : (
        <StructuredObservation.List
          items={visibleMedications}
          getId={(m) => m._clientId}
          getName={(m) => m.name}
          getSubtitle={getDosageSubtitle}
          onDelete={removeMedication}
          addLabel={t({
            id: 'medication.form.addButton',
            message: 'Medicatie toevoegen',
          })}
          onSubmitAndAdvance={handleSubmitAndAdvance}
          isSubmitDisabled={visibleMedications.length === 0 && schemeStatus !== 'confirmed-empty'}
          emptyMessage={
            schemeStatus === 'confirmed-empty'
              ? t({
                  id: 'medication.emptyState.confirmed',
                  message: 'Je neemt geen medicatie',
                })
              : undefined
          }
        >
          <StructuredObservation.ItemDialogue
            title={(mode) =>
              mode === 'update'
                ? t({
                    id: 'medication.form.editTitle',
                    message: 'Medicatie bewerken',
                  })
                : t({
                    id: 'medication.form.addTitle',
                    message: 'Medicatie toevoegen',
                  })
            }
          >
            <MedicationItemForm />
          </StructuredObservation.ItemDialogue>
        </StructuredObservation.List>
      )}
    </MedicationContext.Provider>
  )
}
